import React, { useState, useEffect } from 'react';
import { FaTimes, FaPaperPlane, FaPhone, FaMapMarkerAlt, FaClock, FaBolt } from 'react-icons/fa';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import AlertModal from '../AlertModal/AlertModal';
import './ConversationModal.scss';

const ConversationModal = ({ conversation, onClose, onSendMessage, onMessagesRead }) => {
    const { apiRequest } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [sendFailed, setSendFailed] = useState(false);
    const [triggeringReminder, setTriggeringReminder] = useState(false);
    const [reminderFeedback, setReminderFeedback] = useState(null);
    const messagesEndRef = React.useRef(null);
    const replyInputRef = React.useRef(null);

    const handleClose = () => {
        // Just close without triggering a full reload
        onClose();
    };

    useEffect(() => {
        fetchMessages();
    }, [conversation.id]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (messages.length > 0 && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const fetchMessages = async () => {
        try {
            setLoading(true);
            const response = await apiRequest(
                `/api/ejento/conversations/${conversation.id}/messages${window.location.search}`
            );

            if (response.success) {
                setMessages(response.data.messages || []);
                // Messages are marked as read on backend automatically when fetched
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendReply = async () => {
        if (!replyText.trim() || sending) return;

        try {
            setSending(true);
            const response = await apiRequest(
                `/api/ejento/conversations/${conversation.id}/reply${window.location.search}`,
                {
                    method: 'POST',
                    body: JSON.stringify({ message: replyText })
                }
            );

            if (response.success) {
                setReplyText('');
                await fetchMessages();
                if (onSendMessage) {
                    onSendMessage(conversation.id);
                }
            } else {
                setSendFailed(true);
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            setSendFailed(true);
        } finally {
            setSending(false);
        }
    };

    const handleSendReminderNow = async () => {
        if (!conversation.appointment || triggeringReminder) return;

        try {
            setTriggeringReminder(true);
            setReminderFeedback(null);

            const response = await apiRequest(
                `/api/ejento/conversations/${conversation.id}/send-reminder-now${window.location.search}`,
                {
                    method: 'POST'
                }
            );

            setReminderFeedback({
                type: response.success ? 'success' : 'error',
                message: response.message || (response.success ? 'Reminder sent immediately.' : 'Reminder test failed.')
            });

            await fetchMessages();
            if (onSendMessage) {
                await onSendMessage(conversation.id);
            }
        } catch (error) {
            console.error('Error sending immediate reminder:', error);
            setReminderFeedback({
                type: 'error',
                message: error.message || 'Failed to send reminder immediately.'
            });
        } finally {
            setTriggeringReminder(false);
        }
    };

    const formatPhone = (phone) => {
        if (!phone) return '';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 11 && cleaned.startsWith('1')) {
            return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        }
        return phone;
    };

    const getAppointmentDate = (workDate, timeWindow) => {
        if (!workDate) return timeWindow || '';

        let date = null;
        if (typeof workDate === 'string') {
            const dateMatch = workDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (dateMatch) {
                const [, year, month, day] = dateMatch;
                // Parse the calendar date locally so date-only values do not shift by timezone.
                date = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
            }
        }

        if (!date) {
            date = new Date(workDate);
        }

        if (Number.isNaN(date.getTime())) {
            return timeWindow || '';
        }

        return `${format(date, 'MMM d, yyyy')} - ${timeWindow || 'TBD'}`;
    };

    const conversationModal = (
        <div className="conversation-modal-overlay" onClick={handleClose}>
            <div className="conversation-modal" onClick={(e) => e.stopPropagation()}>
                <div className="conversation-modal__header">
                    <div className="header-info">
                        <h2>{conversation.contact.name}</h2>
                        <div className="contact-details">
                            <span className="phone">
                                <FaPhone /> {formatPhone(conversation.contact.phone)}
                            </span>
                            <span className="location">
                                <FaMapMarkerAlt /> {conversation.contact.location.city}, {conversation.contact.location.state}
                            </span>
                        </div>
                        {conversation.appointment && (
                            <div className="appointment-details">
                                <FaClock /> <strong>{conversation.appointment.serviceDescription}</strong>
                                <span> - {getAppointmentDate(conversation.appointment.workDate, conversation.appointment.timeWindow)}</span>
                            </div>
                        )}
                    </div>
                    <button type="button" className="close-btn" onClick={handleClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="conversation-modal__messages">
                    {loading ? (
                        <div className="loading-state">Loading messages...</div>
                    ) : messages.length === 0 ? (
                        <div className="empty-state">No messages yet</div>
                    ) : (
                        <>
                            {messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`message-bubble ${message.direction === 'outbound' ? 'message-bubble--sent' : 'message-bubble--received'}`}
                                >
                                    <div className="message-content">{message.body}</div>
                                    <div className="message-meta">
                                        <span className="message-time">
                                            {format(new Date(message.timestamp), 'MMM d, h:mm a')}
                                        </span>
                                        {message.direction === 'outbound' && message.status && (
                                            <span className={`message-status message-status--${message.status}`}>
                                                {message.status}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                <div className="conversation-modal__reply">
                    {conversation.appointment && (
                        <div className="conversation-modal__test-actions">
                            <button
                                type="button"
                                className="test-reminder-btn"
                                onClick={handleSendReminderNow}
                                disabled={triggeringReminder || sending}
                            >
                                <FaBolt />
                                {triggeringReminder ? 'Sending Reminder...' : 'Send Reminder Now'}
                            </button>
                            <p className="test-reminder-note">
                                Uses this conversation&apos;s appointment details and sends the scheduled reminder flow immediately.
                            </p>
                            {reminderFeedback && (
                                <div className={`test-reminder-feedback test-reminder-feedback--${reminderFeedback.type}`}>
                                    {reminderFeedback.message}
                                </div>
                            )}
                        </div>
                    )}

                    <textarea
                        ref={replyInputRef}
                        className="reply-input"
                        placeholder="Type your manual reply here..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendReply();
                            }
                        }}
                        disabled={sending}
                        rows={3}
                    />
                    <button
                        type="button"
                        className="send-btn"
                        onClick={handleSendReply}
                        disabled={!replyText.trim() || sending}
                    >
                        <FaPaperPlane />
                        {sending ? 'Sending...' : 'Send Manual Reply'}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {conversationModal}
            <AlertModal
                isOpen={sendFailed}
                onClose={() => setSendFailed(false)}
                title="Message not sent"
                message="Failed to send message. Please try again."
                confirmText="OK"
                variant="error"
                returnFocusRef={replyInputRef}
            />
        </>
    );
};

export default ConversationModal;
